// Article "Baromètre Sécurité Routière — Juillet 2026" — ANGLE PÉDAGOGIQUE
// Source officielle : ONISR — Baromètre mensuel juillet 2026, publié le 07/08/2026.
// PDF officiel local : /documents/barometre-onisr-juillet-2026.pdf
// Contexte : bilan mitigé de l'été — mortalité globale en baisse mais 2RM +7 tués,
// blessés graves en hausse et autoroute +19 %. Ton pédagogique orienté "leçons".

import {
  AlertTriangle,
  Bike,
  Users,
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  Gauge,
  Eye,
  Heart,
  Sun,
  Calendar,
  Quote,
  Lightbulb,
  Route,
  Activity,
} from 'lucide-react';

export const barometreJuillet2026Article = {
  slug: 'barometre-securite-routiere-juillet-2026',
  weekVolume: 35,
  weekYear: 2026,
  monthLabel: 'Août 2026',
  category: 'Actualité Sécurité Routière',
  shortTitle: 'Baromètre Juillet 2026 — Leçons de l\'été',
  title: 'Juillet 2026 : la mortalité baisse, mais les motards restent en alerte',
  kicker: '📊 Baromètre officiel ONISR — Analyse pédagogique',
  subtitle:
    "Le baromètre national de juillet 2026, publié par l'ONISR le 7 août, envoie un signal encourageant : <strong>-24 tués</strong> par rapport à juillet 2025 en France métropolitaine. Mais dans le détail, une catégorie continue de payer un lourd tribut : <strong>les usagers de deux-roues motorisés (+7 tués)</strong>. Décryptage pédagogique pour comprendre où tu vas rouler, et comment ne pas devenir une statistique.",
  heroImage: '/images/articles/autoroute-sunset.jpg?v=20260815',
  heroImageAlt:
    "Motard en silhouette au coucher du soleil estival sur une route dégagée — illustration du baromètre national de la Sécurité Routière de juillet 2026 et des risques accrus pour les deux-roues motorisés en été",
  topRightKicker: { line1: 'Données officielles', line2: 'Juillet 2026 · ONISR' },
  readTimeMin: 7,
  publishedAt: '2026-08-15',
  seoDescription:
    "Baromètre ONISR juillet 2026 : 310 tués en France métropolitaine (-24 vs 2025). Focus pédagogique motards (+7 tués), autoroute (+19 %), 12 mois glissants. Analyse et 6 leçons pour ta première saison moto.",
  seoKeywords:
    'baromètre sécurité routière juillet 2026, ONISR juillet 2026, mortalité motards été 2026, statistiques accidents moto, prévention deux-roues motorisé, blessés graves 2026, autoroute mortalité',
  about:
    "Analyse du baromètre mensuel ONISR de juillet 2026, ton pédagogique orienté 'leçons pour la première saison moto' à destination des apprenants CODE 2 ROUES.",
  source: {
    label: 'ONISR — Baromètre mensuel juillet 2026',
    url: 'https://www.onisr.securite-routiere.gouv.fr/',
    refs:
      "Baromètre mensuel ONISR — juillet 2026. Estimations arrêtées au 07/08/2026 (données provisoires). PDF officiel téléchargeable ci-dessous.",
    pdf: '/documents/barometre-onisr-juillet-2026.pdf',
    pdfName: 'Barometre-ONISR-Juillet-2026.pdf',
  },
  chapo: {
    kicker: 'Pourquoi ce bilan te concerne',
    body:
      "L'été, c'est <strong>ta saison</strong> si tu passes le permis moto. C'est aussi celle où les routes se remplissent : vacances, trajets loisirs, motos qui reprennent la route après l'hiver. Juillet 2026 confirme deux réalités : la mortalité globale <strong>recule enfin</strong> (-24 tués vs juillet 2025), mais les <strong>motards continuent de payer</strong> (+7 tués). Comprendre les chiffres, c'est déjà commencer à s'en protéger.",
    sideStat: {
      kicker: 'Le contraste de l\'été',
      big: '−24 / +7',
      body: "Tous usagers confondus : <strong>24 vies épargnées</strong> par rapport à juillet 2025. Mais chez les motards seuls : <strong>+7 tués</strong>. La moto reste la catégorie qui progresse à l'envers.",
      accent: '#f59e0b',
    },
  },
  pullQuote: {
    text:
      "La baisse globale de juillet est une bonne nouvelle, fruit du travail collectif. Mais elle ne doit pas masquer les fragilités persistantes : deux-roues motorisés, cyclistes, autoroute. La prévention reste une bataille de tous les instants.",
    source: 'Lecture pédagogique du baromètre ONISR — juillet 2026',
  },
  sections: [
    {
      part: 'Partie 1 — Les chiffres officiels',
      title: 'Juillet 2026 : ce que dit vraiment le baromètre',
      intro:
        "Voici les <strong>chiffres officiels</strong> publiés par l'ONISR le 7 août 2026, arrêtés au moment de la publication. Tous concernent la <strong>France métropolitaine</strong>, sauf mention outre-mer.",
      videoEmbed: {
        videoId: '5E-ITHJPj1k',
        title: 'Baromètre Sécurité Routière — Juillet 2026',
        channel: 'Sécurité Routière',
        testid: 'onisr-juillet-2026',
        caption:
          "Vidéo officielle de présentation du baromètre mensuel — bilan de juillet 2026.",
        credit: 'Sécurité Routière · ONISR',
      },
      cards: [
        {
          icon: Heart,
          accent: '#16a34a',
          kicker: 'Mortalité totale',
          title: '310 tués (−24)',
          body:
            "En métropole, <strong>310 personnes sont décédées</strong> sur les routes en juillet 2026, contre 334 en juillet 2025. Soit <strong>−24 tués (−7 %)</strong>. C'est la première vraie inflexion depuis avril.",
        },
        {
          icon: Bike,
          accent: '#cf2031',
          kicker: 'Deux-roues motorisé',
          title: '+7 motards tués',
          body:
            "<strong>Seule catégorie en hausse</strong> avec les seniors. Les 2RM restent la catégorie qui souffre le plus : <strong>~5 300 blessés graves</strong> sur 12 mois, soit un tiers des blessés graves totaux.",
        },
        {
          icon: Users,
          accent: '#16a34a',
          kicker: 'Piétons & autres',
          title: '−14 piétons · −10 auto',
          body:
            "Baisses notables : <strong>-14 piétons</strong>, <strong>-10 automobilistes</strong>, <strong>-5 cyclistes</strong>, <strong>-5 usagers EDPm</strong> (trottinettes). Les campagnes ciblées commencent à payer.",
        },
        {
          icon: TrendingUp,
          accent: '#cf2031',
          kicker: 'Blessés graves',
          title: '1 751 (en hausse)',
          body:
            "Le paradoxe de l'été : <strong>moins de morts, mais plus de blessés graves</strong>. +14 % piétons, +9 % EDPm, +3 % auto, +2 % 2RM. Les accidents sont moins mortels mais plus fréquents.",
        },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: Route,
          accent: '#f59e0b',
          body:
            "<strong>Info route :</strong> la mortalité est <strong>stable hors agglomération</strong>, <strong>en baisse en agglomération</strong> (-14 tués) et <strong>sur autoroute</strong> (-8 tués sur juillet seul). Mais attention : sur le cumul mai-juillet 2026, l'autoroute affiche <strong>+19 %</strong> — c'est LE point noir de l'été.",
        },
      ],
    },
    {
      part: 'Partie 2 — L\'analyse 12 mois glissants',
      title: 'Le vrai thermomètre : les 12 derniers mois',
      intro:
        "Un mois seul ne suffit pas à conclure : les tendances lourdes s'observent sur <strong>12 mois glissants</strong>. Voici ce que révèle l'ONISR pour la période d'août 2025 à juillet 2026 (mortalité toutes catégories).",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: 'Automobilistes',
          title: '1 562 tués · +2 %',
          intro:
            "Toujours <strong>près de la moitié</strong> de la mortalité routière. En baisse de -4 % vs 2019 (année de référence de la décennie).",
          points: [
            { ok: false, text: 'Ceinture parfois oubliée en trajet court' },
            { ok: false, text: 'Somnolence sur autoroute (juillet-août)' },
            { ok: true, text: 'Vitesse mieux respectée qu\'il y a 5 ans' },
          ],
        },
        {
          accent: '#16a34a',
          kicker: 'Deux-roues motorisés',
          title: '682 tués · −3 %',
          intro:
            "<strong>Bonne nouvelle</strong> sur 12 mois : la mortalité 2RM recule (-3 % vs an dernier, -9 % vs 2019). Mais reste ~2 % du trafic pour ~22 % des morts.",
          points: [
            { ok: true, text: 'Effet formation A2 renforcée (2024)' },
            { ok: true, text: 'Diffusion des EPI Airbag' },
            { ok: false, text: 'Blessés graves 2RM en hausse (+3 %)' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'Cyclistes',
          title: '250 tués · +11 %',
          intro:
            "<strong>Alerte forte</strong> : +11 % vs 12 mois précédents, <strong>+34 % vs 2019</strong>. La pratique explose, la prévention peine à suivre.",
          points: [
            { ok: false, text: 'Boom des vélos électriques (VAE)' },
            { ok: false, text: 'Casque toujours non obligatoire adulte' },
            { ok: false, text: 'Piste cyclable ≠ zéro risque' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'EDPm (trottinettes)',
          title: '84 tués · +17 %',
          intro:
            "<strong>Nouveau danger urbain</strong> : trottinettes électriques, gyropodes. +17 % sur un an, +32 % de blessés graves.",
          points: [
            { ok: false, text: 'Vitesse bridée à 25 km/h peu respectée' },
            { ok: false, text: 'Pas de code de la route obligatoire' },
            { ok: false, text: 'Sous-équipement (casque, gants)' },
          ],
        },
      ],
    },
    {
      part: 'Partie 3 — Focus âges',
      title: 'Seniors en hausse, jeunes en baisse : le grand écart',
      intro:
        "Le baromètre juillet 2026 confirme une <strong>bascule démographique</strong> : les 18-24 ans progressent enfin, mais les seniors dépassent les jeunes dans la mortalité routière.",
      cards: [
        {
          icon: TrendingDown,
          accent: '#16a34a',
          kicker: 'Moins de 18 ans',
          title: '−7 tués (juillet)',
          body:
            "Baisse encourageante. Les campagnes en collège (ASSR 1 & 2) et l'attention sur les cyclos AM (Brevet Sécurité Routière) portent leurs fruits. <strong>Continue à réviser l'ASSR</strong> même après ton permis.",
        },
        {
          icon: TrendingDown,
          accent: '#16a34a',
          kicker: '18-24 ans',
          title: '−8 tués (juillet)',
          body:
            "<strong>Bonne nouvelle</strong> pour la tranche traditionnellement la plus exposée. -7 % vs 2019 sur 12 mois. Mais reste 511 morts en un an pour <strong>8 % de la population</strong> = surrisque net.",
        },
        {
          icon: TrendingDown,
          accent: '#16a34a',
          kicker: '25-64 ans',
          title: '−16 tués (juillet)',
          body:
            "La <strong>plus grosse baisse</strong> en valeur absolue. Cette tranche représente la majorité des motards actifs — c'est bon signe pour la communauté 2RM.",
        },
        {
          icon: TrendingUp,
          accent: '#cf2031',
          kicker: '65 ans et plus',
          title: '+7 tués (juillet)',
          body:
            "<strong>Seule tranche d'âge en hausse.</strong> 962 morts sur 12 mois (+6 %). Fragilité physique + retour à la moto post-retraite = combinaison à risque. Pense à tes parents/grands-parents.",
        },
      ],
      cardsCols: 2,
      callouts: [
        {
          icon: Activity,
          accent: '#7c3aed',
          body:
            "<strong>À noter :</strong> les <strong>18-24 ans</strong> restent la tranche la plus à risque en valeur relative. 3 000 blessés graves en un an, +5 % vs 2019. Si tu es dans cette tranche : ton permis n'est <em>que</em> le début. La formation continue (stage post-permis, journée circuit) reste essentielle.",
        },
      ],
    },
    {
      part: 'Partie 4 — Les 6 leçons pédagogiques',
      title: '6 leçons à emporter dans ton sac de casque',
      intro:
        "Ce baromètre n'est pas qu'un tableau de chiffres : c'est un <strong>manuel de survie routière</strong>. Voici les 6 leçons concrètes que tu peux appliquer <strong>dès ta prochaine leçon d'auto-école ou ta prochaine sortie moto</strong>.",
      cards: [
        {
          icon: Sun,
          accent: '#16a34a',
          kicker: 'Leçon n°1',
          title: 'L\'été, prudence multipliée',
          body:
            "Juillet = pic de trafic loisir + touristes désorientés + motos sorties de garage. <strong>Anticipe les comportements imprévisibles</strong>. Regarde <em>où regardent</em> les autres conducteurs — un touriste cherche sa route, il ne te voit pas.",
        },
        {
          icon: Route,
          accent: '#cf2031',
          kicker: 'Leçon n°2',
          title: 'Autoroute : +19 %, pas anodin',
          body:
            "Sur 3 mois, la mortalité autoroute a bondi de <strong>+19 %</strong>. Vitesses élevées + monotonie + fatigue estivale = combo fatal. <strong>Pause toutes les 2h</strong>, hydrate-toi, et méfie-toi de la 'somnolence de 14h' après le déjeuner.",
        },
        {
          icon: Gauge,
          accent: '#cf2031',
          kicker: 'Leçon n°3',
          title: 'Blessés graves : le vrai signal',
          body:
            "Moins de morts, <strong>mais +6 % de blessés graves</strong> sur 12 mois. Ta moto pardonne moins que ta voiture : un choc à 50 km/h sans EPI = fractures, brûlures, séquelles à vie. <strong>EPI complet = non négociable</strong>.",
        },
        {
          icon: Eye,
          accent: '#16a34a',
          kicker: 'Leçon n°4',
          title: 'Être vu, encore et toujours',
          body:
            "Sur les 682 motards tués sur 12 mois, <strong>~35 % sont dus à un défaut de vigilance de l'automobiliste</strong>. Feux de croisement en permanence, gilet réfléchissant en interurbain, position centrale dans ta voie. <strong>Fais-toi voir avant de rouler vite</strong>.",
        },
        {
          icon: Users,
          accent: '#f59e0b',
          kicker: 'Leçon n°5',
          title: 'Les autres 2 roues aussi',
          body:
            "Cyclistes (+11 %) et EDPm (+17 %) explosent. En ville, ils vont <strong>surgir de partout</strong> : bandes cyclables croisées, sas vélo, remontées de file. Attends-toi à l'inattendu à chaque intersection. Le contact visuel est ton meilleur ami.",
        },
        {
          icon: Heart,
          accent: '#16a34a',
          kicker: 'Leçon n°6',
          title: 'La bonne nouvelle : ça marche',
          body:
            "Sur 12 mois, la mortalité 2RM recule de <strong>-3 %</strong> et de <strong>-9 % vs 2019</strong>. La formation A2, les EPI Airbag, les stages post-permis fonctionnent. <strong>Reste dans le mouvement</strong> : révise ETM régulièrement sur CODE 2 ROUES, forme-toi, continue à apprendre.",
        },
      ],
      cardsCols: 3,
    },
    {
      part: 'Partie 5 — Zoom pédagogique',
      title: 'Pourquoi les motards restent la catégorie à surveiller ?',
      intro:
        "Trois raisons se combinent — et connaître ces raisons, c'est déjà savoir comment les <strong>déjouer</strong>.",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: 'Raison n°1',
          title: 'Vulnérabilité physique',
          intro:
            "Aucune coque, aucun airbag frontal, aucune ceinture. <strong>Ton corps est le pare-chocs</strong>.",
          points: [
            { ok: false, text: 'Choc à 50 km/h sans EPI = risque vital' },
            { ok: false, text: 'Le sol arrive vite quand on tombe' },
            { ok: true, text: 'EPI CE + airbag = -50 % de blessures graves' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'Raison n°2',
          title: 'Visibilité réduite',
          intro:
            "Une moto est <strong>2 fois moins visible</strong> qu'une voiture depuis un rétroviseur.",
          points: [
            { ok: false, text: 'Angle mort auto = piège classique' },
            { ok: false, text: 'Contre-jour aggrave le camouflage' },
            { ok: true, text: 'Feux allumés + gilet = +40 % visibilité' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'Raison n°3',
          title: 'Prise de risque',
          intro:
            "Le facteur comportemental reste <strong>~85 % des accidents mortels moto</strong>.",
          points: [
            { ok: false, text: 'Vitesse : 1re cause moto' },
            { ok: false, text: 'Alcool : ~25-30 % des morts moto' },
            { ok: true, text: "Formation continue = ta meilleure protection" },
          ],
        },
      ],
    },
  ],
  quiz: [
    {
      id: 'q1',
      question: "Combien de personnes sont décédées sur les routes de France métropolitaine en juillet 2026 ?",
      options: ['258', '310', '334', '428'],
      correctIndex: 1,
      explain:
        "310 personnes sont décédées sur les routes métropolitaines en juillet 2026, soit -24 tués par rapport à juillet 2025 (334). C'est une baisse encourageante de -7 %.",
    },
    {
      id: 'q2',
      question: "Quelle est la SEULE catégorie d'usagers dont la mortalité augmente en juillet 2026 vs juillet 2025 ?",
      options: ['Piétons', 'Automobilistes', 'Cyclistes', 'Usagers de deux-roues motorisé'],
      correctIndex: 3,
      explain:
        "Les usagers de deux-roues motorisés enregistrent +7 tués en juillet 2026. Toutes les autres catégories (piétons, auto, EDPm, cyclistes) sont en baisse. Signal d'alerte pour les motards.",
    },
    {
      id: 'q3',
      question: "Sur le cumul mai à juillet 2026, l'autoroute affiche quelle évolution de mortalité ?",
      options: ['−19 %', 'Stable', '+9 %', '+19 %'],
      correctIndex: 3,
      explain:
        "La mortalité sur autoroute a bondi de +19 % sur le trimestre mai-juillet 2026. Combo vitesses élevées + trafic estival + fatigue = point noir de l'été. Pense pause toutes les 2h.",
    },
    {
      id: 'q4',
      question: "Sur les 12 derniers mois glissants, quelle catégorie enregistre +34 % de tués par rapport à 2019 ?",
      options: ['Motards', 'Cyclistes', 'Piétons', 'Automobilistes'],
      correctIndex: 1,
      explain:
        "Les cyclistes affichent +34 % de tués sur 12 mois par rapport à 2019 (250 morts). L'engouement pour le vélo urbain et le VAE dépasse largement la prévention actuelle.",
    },
    {
      id: 'q5',
      question: "Quelle tranche d'âge voit sa mortalité AUGMENTER en juillet 2026 ?",
      options: ['Moins de 18 ans', '18-24 ans', '25-64 ans', '65 ans et plus'],
      correctIndex: 3,
      explain:
        "Les 65 ans et plus enregistrent +7 tués en juillet 2026 (et +6 % sur 12 mois). Fragilité physique et retour à la conduite/moto post-retraite les rendent vulnérables. Toutes les autres tranches baissent.",
    },
    {
      id: 'q6',
      question: "Sur 12 mois glissants, la mortalité des deux-roues motorisés évolue de :",
      options: ['+7 % vs 2019', '+3 % vs 12 mois précédents', '−3 % vs 12 mois précédents', '−15 % vs 2019'],
      correctIndex: 2,
      explain:
        "Bonne nouvelle sur 12 mois : la mortalité 2RM recule de -3 % vs l'année précédente et de -9 % vs 2019. Les efforts de formation (A2 renforcée, stages) commencent à payer — mais les blessés graves restent en hausse.",
    },
    {
      id: 'q7',
      question: "Quel est le paradoxe majeur du baromètre juillet 2026 ?",
      options: [
        "Plus de morts mais moins de blessés",
        "Moins de morts mais plus de blessés graves",
        "Baisse à Paris, hausse en province",
        "Baisse chez les jeunes, hausse chez les motards uniquement",
      ],
      correctIndex: 1,
      explain:
        "Le vrai signal : moins de morts (-24) MAIS +6 % de blessés graves sur 12 mois. Les accidents sont moins mortels mais plus fréquents et plus graves. C'est pour ça que l'EPI complet reste vital.",
    },
  ],
  ctaFinal: {
    kicker: "Passe à l'action pédagogique",
    title: "Transforme ces chiffres en vigilance concrète",
    body:
      "Chaque question de l'examen ETM 2026 sur code2roues.fr est calibrée à partir des données officielles de l'ONISR. Réviser régulièrement, c'est intégrer les bons réflexes <em>avant</em> de croiser la situation à risque. <strong>100 % gratuit, sans pub, sans tracking.</strong>",
    primary: { label: 'Explorer un autre dossier magazine', to: '/magazine' },
    secondary: { label: 'Consulter le livret plateau', to: '/livret-plateau' },
  },
};
